import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { locationsAPI } from '../../../api/bookings';

export function useAnalytics() {
  const [summary, setSummary] = useState(null);
  const [locations, setLocations] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [authBreakdown, setAuthBreakdown] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pocket Pay Reconciliation export — separate "Payment Date Range"
  // (Brunei time), distinct from the booking-date filters above, since
  // this report is scoped by when the payment was PROCESSED, not when
  // the booking/court date is.
  const [pocketDateFrom, setPocketDateFrom] = useState('');
  const [pocketDateTo, setPocketDateTo] = useState('');
  const [exportingPocket, setExportingPocket] = useState(false);

  const loadLocations = async () => {
    try {
      const locs = await locationsAPI.getAllLocations();
      setLocations(locs);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const loadAuthBreakdown = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/admin/analytics/auth-breakdown`, {
        credentials: 'include',
      });
      if (response.ok) setAuthBreakdown(await response.json());
    } catch (error) {
      console.error('Failed to load auth breakdown:', error);
    }
  };

  const loadPaymentStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${API_URL}/api/admin/payments/stats`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentStats(data);
      }
    } catch (error) {
      console.error('Failed to load payment stats:', error);
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const params = new URLSearchParams();

      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (locationFilter && locationFilter !== 'all') params.append('location_id', locationFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/admin/analytics/summary?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSummary(data);
    } catch (error) {
      console.error('Analytics summary error:', error);
      toast.error('Failed to load analytics summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Deliberate one-time bootstrap: date/location/status filters are
    // applied explicitly via the "Apply Filters" button (see
    // applyFilters/clearFilters below), not automatically when they
    // change — so this effect intentionally does not react to them.
    loadLocations();
    loadSummary();
    loadPaymentStats();
    loadAuthBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();

      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (locationFilter && locationFilter !== 'all') params.append('location_id', locationFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/admin/analytics/export?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'bookings_report.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const applyFilters = () => {
    loadSummary();
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setLocationFilter('all');
    setStatusFilter('all');
    setTimeout(() => loadSummary(), 100);
  };

  const handleExportPocketReconciliation = async () => {
    setExportingPocket(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();

      if (pocketDateFrom) params.append('payment_date_from', pocketDateFrom);
      if (pocketDateTo) params.append('payment_date_to', pocketDateTo);

      const response = await fetch(`${API_URL}/api/admin/analytics/export-pocket-reconciliation?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'pocket_reconciliation.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Pocket Reconciliation report exported!');
    } catch (error) {
      toast.error('Failed to export Pocket Reconciliation report');
    } finally {
      setExportingPocket(false);
    }
  };

  return {
    summary, locations, paymentStats, loading, exporting, authBreakdown,
    dateFrom, setDateFrom, dateTo, setDateTo, locationFilter, setLocationFilter,
    statusFilter, setStatusFilter,
    pocketDateFrom, setPocketDateFrom, pocketDateTo, setPocketDateTo, exportingPocket,
    handleExport, applyFilters, clearFilters, handleExportPocketReconciliation,
  };
}
